import React, { useState } from "react";
import { formatDay, initialsName, mergeMissingResultRow } from "../../api/shape.js";
import { LINE_LABEL, unapprovedRecordsText } from "./text.js";

/**
 * Step 1, "Check the data": the three readiness lines as buttons (design
 * Main.dc.html), each opening what is behind it (the owner-approved
 * drill-down, DEC-CHAT-20261007-412). Each line's `items` comes straight from
 * `readinessView` (azure-functions/cis/shared/cockpit.js §4.6) -- never
 * invented beyond what that line carries:
 *  - `inspection_photos` items: Remind inspector / Accept without photo, each
 *    through the screen's inline confirm step (never a browser dialog).
 *  - `inspection_results` items: the missing-result rows with the existing
 *    results reminder and "Open in Plan".
 *  - `ncr_decisions` items: "Open NCR" hands off to the NCR screen.
 * A green line (nothing behind it) shows its all-clear sentence.
 */
const LINE_DETAIL_CLEAR_TEXT = {
  inspection_results: "Every booked inspection this week has a result.",
  inspection_photos: "Every inspection has a photo or an acceptance.",
  ncr_decisions: "No NCRs are awaiting a decision this week.",
};
const LINE_DETAIL_CLEAR_TEXT_PERIOD = {
  inspection_results: "Every booked inspection in this period has a result.",
  inspection_photos: "Every inspection has a photo or an acceptance.",
  ncr_decisions: "No NCRs are awaiting a decision in this period.",
};

/**
 * "Results missing" rows. The readiness payload carries the schedule ref and
 * (newer backends) title, date, inspector and trade; older ones fall back to
 * the Today data the app already holds (resolveMissingResult). "Remind
 * inspector" is the existing results reminder (POST /api/cockpit/remind) with
 * its outcome shown inline.
 */
function MissingResultRow({ code, item, resolve, onRemind, onNavigate }) {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const row = mergeMissingResultRow(item, resolve ? resolve(code, item.scheduleRef) : null);
  async function remind() {
    if (!onRemind) return;
    setPending(true);
    const outcome = await onRemind({ code, scheduleRef: item.scheduleRef });
    setPending(false);
    setResult(outcome);
  }
  const inspector = row ? (row.assignedTo ? initialsName(row.assignedTo) : "Unassigned") : null;
  return (
    <div className="rp-photo-row" data-testid={`report-missing-row-${code}-${item.scheduleRef}`} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 11.5 }}>
      <span style={{ color: "var(--t1)" }}>{row ? row.title || "Inspection" : "Inspection"}</span>
      {row && row.plannedDate ? <span className="mono" style={{ color: "var(--t3)" }}>planned {formatDay(row.plannedDate)}</span> : null}
      {row && row.category ? <span style={{ color: "var(--t3)" }}>{row.category}</span> : null}
      {inspector ? <span style={{ color: "var(--t2)" }}>{inspector}</span> : null}
      {!row ? <span className="mono" style={{ color: "var(--t3)" }}>{item.scheduleRef}</span> : null}
      <span className="rp-break" />
      <button type="button" className="btn off" style={{ minHeight: 24, fontSize: 11 }} disabled={pending || !onRemind} onClick={remind}>
        {pending ? "…" : "Remind inspector"}
      </button>
      <button
        type="button"
        className="btn off"
        style={{ minHeight: 24, fontSize: 11 }}
        disabled={!onNavigate || !(row && row.plannedDate)}
        title={row && row.plannedDate ? undefined : "This inspection is not in the loaded Today data"}
        onClick={() => onNavigate && onNavigate("plan", { openItem: { code, scheduleRef: item.scheduleRef, plannedDate: row.plannedDate } })}
      >
        Open in Plan
      </button>
      {result ? <span className={`remind-result ${result.ok ? "ok" : "warn"}`}>{result.message}</span> : null}
    </div>
  );
}

function ChecklistDetail({ line, code, onNavigate, onRemindPhoto, onWaivePhoto, resolveMissingResult, onRemindResult, periodView = false }) {
  const clearText = periodView ? LINE_DETAIL_CLEAR_TEXT_PERIOD : LINE_DETAIL_CLEAR_TEXT;
  const items = line.items || [];
  if (!items.length) {
    // A red line (count > 0) with an empty `items` array is never "nothing
    // behind this line": readinessView carries the count but no list for it.
    // Say so, rather than claim the opposite.
    return (
      <div className="rp-detail-list" data-testid={`report-detail-${code}-${line.id}`} style={{ fontSize: 12, color: line.green ? "var(--t3)" : "var(--wr-bad)", marginTop: 2 }}>
        {line.green
          ? clearText[line.id] || "Nothing to show."
          : `${line.count} ${(LINE_LABEL[line.id] || line.id).toLowerCase()} — list not available yet.`}
      </div>
    );
  }
  if (line.id === "inspection_photos") {
    // SPEC-CIS-COCKPIT-READINESS-ACTIONS-001 §4.1/§4.3/§4.4: each row names
    // the inspection (title, date, inspector, category) and carries the two
    // site-manager actions.
    return (
      <div data-testid={`report-detail-${code}-${line.id}`} style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }} className="rp-detail-list">
        {items.map((item) => (
          <div key={item.inspectionNumber} className="rp-photo-row" data-testid={`report-photo-row-${code}-${item.inspectionNumber}`} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 11.5 }}>
            <span className="mono" style={{ color: "var(--t3)" }}>CI-{item.inspectionNumber}</span>
            <span style={{ color: "var(--t1)" }}>{item.title || "Inspection"}</span>
            <span className="rp-break" />
            {item.inspectionDate ? <span className="mono" style={{ color: "var(--t3)" }}>{item.inspectionDate}</span> : null}
            {item.category ? <span style={{ color: "var(--t3)" }}>{item.category}</span> : null}
            <span style={{ color: "var(--t2)" }}>{item.inspector && item.inspector.upn ? item.inspector.upn : "no inspector assigned"}</span>
            <span className="rp-break" />
            <button
              type="button"
              className="btn off"
              style={{ minHeight: 24, fontSize: 11 }}
              disabled={!onRemindPhoto || !(item.inspector && item.inspector.upn)}
              title={item.inspector && item.inspector.upn ? "Email the inspector to add a photo" : "No inspector is assigned to this inspection"}
              onClick={() => onRemindPhoto && onRemindPhoto(item)}
            >
              Remind inspector
            </button>
            <button
              type="button"
              className="btn off"
              style={{ minHeight: 24, fontSize: 11 }}
              disabled={!onWaivePhoto || !item.etag}
              title="Record a reason and accept this inspection without a photo"
              onClick={() => onWaivePhoto && onWaivePhoto(item)}
            >
              Accept without photo
            </button>
          </div>
        ))}
      </div>
    );
  }
  if (line.id === "inspection_results") {
    return (
      <div data-testid={`report-detail-${code}-${line.id}`} style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }} className="rp-detail-list">
        <div data-testid="report-missing-helper" style={{ fontSize: 11, color: "var(--t3)" }}>
          The inspector records the result in the capture app. If the inspection did not happen, move or cancel it in Plan.
        </div>
        {items.map((item) => (
          <MissingResultRow key={item.scheduleRef} code={code} item={item} resolve={resolveMissingResult} onRemind={onRemindResult} onNavigate={onNavigate} />
        ))}
      </div>
    );
  }
  if (line.id === "ncr_decisions") {
    return (
      <div data-testid={`report-detail-${code}-${line.id}`} style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
        {items.map((item) => (
          <div key={item.ncrNumber} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 11.5 }}>
            <span style={{ color: "var(--t2)" }}>{item.title || `NCR D${item.ncrNumber}`}</span>
            <button
              type="button"
              className="btn acc"
              style={{ minHeight: 24, fontSize: 11 }}
              onClick={() => onNavigate && onNavigate("ncrs", { code, ncrNumber: item.ncrNumber })}
            >
              Open NCR
            </button>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div data-testid={`report-detail-${code}-${line.id}`} style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      {items.map((item) => (
        <span key={item.scheduleRef} className="mono" style={{ fontSize: 11.5, color: "var(--t2)" }}>{item.scheduleRef}</span>
      ))}
    </div>
  );
}

/**
 * Non-blocking reminders (DEC-CHAT-20260930-203): older `Recorded` NCRs that
 * no longer block this week's report. Renders nothing when the list is empty.
 */
function ReportReminders({ reminders, code, onNavigate }) {
  if (!reminders || !reminders.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 4 }}>
      <div style={{ fontSize: 11, color: "var(--t3)" }}>Older NCRs still open:</div>
      {reminders.map((item) => (
        <button
          key={item.ncrNumber}
          type="button"
          className="mono"
          onClick={() => onNavigate && onNavigate("ncrs", { code, ncrNumber: item.ncrNumber })}
          style={{
            fontSize: 10.5, color: "var(--t3)", background: "none", border: "1px solid var(--line2)",
            borderRadius: 4, padding: "1px 6px", cursor: "pointer",
          }}
          title={item.title || `NCR D${item.ncrNumber}`}
        >
          D{item.ncrNumber}
        </button>
      ))}
    </div>
  );
}

/**
 * The step-1 lines. Each line is a real button (`aria-expanded`,
 * `aria-controls`); the open one shows its drill-down right under it.
 */
export function ReadinessLines({ project, openLine, onToggle, detailProps }) {
  return (
    <>
      <div className="wr-lines" data-testid={`report-lines-${project.code}`}>
        {project.checklist.map((line) => {
          const bad = !line.green;
          const open = openLine === line.id;
          const detailId = `wr-detail-${project.code}-${line.id}`;
          return (
            <React.Fragment key={line.id}>
              <button
                type="button"
                className="wr-line"
                data-bad={bad ? "true" : "false"}
                data-testid={`report-line-${project.code}-${line.id}`}
                aria-expanded={open}
                aria-controls={detailId}
                onClick={() => onToggle(line.id)}
              >
                <span>
                  <span className="wr-ldot" aria-hidden="true" />
                  {LINE_LABEL[line.id] || line.label}
                </span>
                <span className="wr-lcount">{bad ? `${line.count} to fix` : "clear"}</span>
              </button>
              {open ? (
                <div id={detailId} className="wr-detail">
                  <ChecklistDetail line={line} code={project.code} periodView={Boolean(project.period)} {...detailProps} />
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
      {project.unapprovedRecords > 0 ? (
        <div data-testid={`report-readiness-unapproved-${project.code}`} style={{ fontSize: 12, color: "var(--t2)", marginTop: 4 }}>
          {unapprovedRecordsText(project.unapprovedRecords)}{" "}
          <a href="#today" data-testid={`report-readiness-unapproved-link-${project.code}`} onClick={(e) => { e.preventDefault(); if (detailProps.onNavigate) detailProps.onNavigate("endofday"); }}>Go to End of day</a>
        </div>
      ) : null}
      <ReportReminders reminders={project.reminders} code={project.code} onNavigate={detailProps.onNavigate} />
    </>
  );
}
